import {
  Col,
  Form,
  Input,
  Label,
  ModalBody,
  FormGroup,
  InputGroupText,
  InputGroup,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { P, H5 } from "../../../../../AbstractElements";
import { useState, Fragment, useCallback } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import Dropzone from "react-dropzone";
import Swal from "sweetalert2";
import { Upload } from "react-feather";

//TYPE AND SERVICES
import {
  ATTACHMENT_CATEGORY_QUERY_KEY,
  attachmentCategoryService,
} from "../../../../../Services/attachmentCategoryService";

import {
  APPLICANT_ATTACHMENTS_QUERY_KEY,
  applicantAttachmentsService,
} from "../../../../../Services/applicantsattachmentService";

//TYPES
import { AttachmentCategory } from "../../../../../Types/Attachments/AttachmentCategory";

//INTERFACE
interface UpdateFormProps {
  toggle?: () => void;
  ApplNo: string;
}

const UploadAttachmentForm: React.FC<UpdateFormProps> = ({
  toggle,
  ApplNo,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);
  const [files, setFiles] = useState([]);

  //GET ATTACHMENT CATEGORY
  const { data: categoryData, isLoading: CatagoryisLoading } = useQuery<{
    data: AttachmentCategory[];
  }>({
    queryKey: ATTACHMENT_CATEGORY_QUERY_KEY,
    queryFn: attachmentCategoryService.getAttachmentCategories,
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadedFiles(acceptedFiles);
  }, []);

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "ppt":
      case "pptx":
        return "📈";
      default:
        return "📁";
    }
  };

  const category: AttachmentCategory[] =
    categoryData?.data?.filter((item) => item.status === 1) ?? [];

  const [formData, setFormData] = useState({
    category_id: "",
    application_no: ApplNo,
  });

  const uploadAttachmentsMutation = useMutation<
    any,
    any, // error type
    FormData // input type for .mutate()
  >({
    mutationFn: (formData: FormData) =>
      applicantAttachmentsService.uploadAttachment(formData), // pass FormData
    onSuccess: () => {
      queryClient.invalidateQueries();
      if (toggle) {
        toggle();
      }
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "File has been uploaded successfully.",
        confirmButtonColor: "#4e73df",
      });
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      let errorMessage = "Failed to attach file. Please check your inputs.";

      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        errorMessage = responseData.errors[firstErrorKey][0];
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }

      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Transaction Failed",
        text: errorMessage,
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const data = new FormData();

    if (uploadedFiles.length > 0) {
      data.append("application_no", ApplNo);
      data.append("attachment_file", uploadedFiles[0]);
      data.append("category_id", formData.category_id);
      data.append("_method", "POST");

      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to attach this file?",
        icon: "question",
        showCancelButton: true,

        confirmButtonText: '<i class="fa fa-upload"></i> Attach File',
        cancelButtonText: '<i class="fa fa-times"></i> Cancel',

        confirmButtonColor: "#4e73df", // Primary
        cancelButtonColor: "#dc3545", // Danger
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Uploading...",
            text: "Please wait while your file is being uploaded.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          uploadAttachmentsMutation.mutate(data);
        }
      });
    } else {
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "No Attched File",
        text: "Please select a file to upload before continuing.",
        confirmButtonColor: "#4e73df",
      });
    }
  };

  return (
    <ModalBody className="mt-0">
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <Col md="12">
            <FormGroup>
              <Label>
                Attachment Category <b className="text-danger">*</b>
              </Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-clip txt-primary"></i>
                </InputGroupText>
                <Input
                  type="select"
                  id="category_id"
                  name="category_id"
                  disabled={CatagoryisLoading ? true : false}
                  required
                  onChange={handleInputChange}
                >
                  <option value="" disabled selected>
                    {CatagoryisLoading
                      ? "Loading..."
                      : "- Select Attachment Category -"}
                  </option>
                  {!CatagoryisLoading &&
                    category.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Input>
              </InputGroup>
            </FormGroup>
          </Col>

          <Col md="12 mt-0">
            <Label>
              Attachment File <b className="text-danger">*</b>
            </Label>

            <Fragment>
              <Dropzone
                onDrop={onDrop}
                maxFiles={1}
                multiple={false}
                maxSize={5 * 1024 * 1024} // 5 MB
                accept={{
                  "image/jpeg": [".jpg", ".jpeg"],
                  "image/png": [".png"],
                  "application/pdf": [".pdf"],
                  "application/msword": [".doc"],
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                    [".docx"],
                  "application/vnd.ms-excel": [".xls"],
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    [".xlsx"],
                  "application/vnd.ms-powerpoint": [".ppt"],
                  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
                    [".pptx"],
                }}
                onDropRejected={(fileRejections) => {
                  fileRejections.forEach(({ file, errors }) => {
                    errors.forEach((error) => {
                      if (error.code === "file-too-large") {
                        Swal.fire({
                          icon: "error",
                          title: "File Too Large",
                          text: `"${file.name}" exceeds the maximum file size of 5 MB.`,
                        });
                      } else {
                        Swal.fire({
                          icon: "error",
                          title: "Invalid File",
                          text: error.message,
                        });
                      }
                    });
                  });
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps()}
                    style={{
                      border: "2px dashed #d6d6d6",
                      borderRadius: 10,
                      padding: 30,
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input {...getInputProps()} />

                    {uploadedFiles.length === 0 ? (
                      <>
                        <h5>📁 Drag & Drop your file here</h5>

                        <p className="text-muted">
                          or <strong>Click to Browse</strong>
                        </p>

                        <small>
                          JPG, JPEG, PNG, PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX
                          <br />
                          <span className="text-muted">
                            Maximum file size: <strong>5 MB</strong>
                          </span>
                        </small>
                      </>
                    ) : (
                      uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 20,
                            border: "1px solid #eee",
                            borderRadius: 8,
                            padding: 15,
                          }}
                        >
                          {file.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 80,
                                height: 80,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontSize: 40,
                                background: "#f5f5f5",
                                borderRadius: 8,
                              }}
                            >
                              {getFileIcon(file)}
                            </div>
                          )}

                          <div style={{ flex: 1, textAlign: "left" }}>
                            <h6>{file.name}</h6>

                            <small className="text-muted">
                              {(file.size / 1024).toFixed(2)} KB
                            </small>
                          </div>

                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </Dropzone>
            </Fragment>
          </Col>

          <Btn
            color="primary"
            className="w-20"
            type="submit"
            disabled={uploadAttachmentsMutation.isPending}
          >
            <Upload size={16} className="me-1" />
            {uploadAttachmentsMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Uploading Attachment File...
              </>
            ) : (
              "Upload"
            )}
          </Btn>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UploadAttachmentForm;
